import type { CatalogListResponse } from "@/modules/cf-feed/presentation/api/types";
import { config } from "@/shared/lib/config";
import { handleError } from "@/shared/lib/fetcher";
import type { PriceInput } from "../../domain/contracts/live-workspace";
import { WorkspaceRequestError } from "./workspace-client";

export type ProductSet = {
  id: string;
  pageId: string;
  name: string;
  updatedAt: string;
  items: Array<{ variantId: string; price: PriceInput }>;
};
export type ProductSetInput = {
  pageId: string;
  name: string;
  items: Array<{ variantId: string; price: PriceInput }>;
};

async function request<T>(
  path: string,
  method: "GET" | "POST" | "PATCH" | "DELETE",
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(
    `${config.apiUrl}/v1/live-workspaces/product-sets${path}`,
    {
      method,
      credentials: "include",
      signal,
      ...(body === undefined
        ? {}
        : {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }),
    },
  );
  if (response.status === 401 || response.status === 403)
    return handleError(response);
  const data = await response.json();
  if (!response.ok)
    throw new WorkspaceRequestError(
      data.code ?? "REQUEST_FAILED",
      response.status,
      data.details,
    );
  return data as T;
}

export const productSetApi = {
  list: (pageId: string) =>
    request<ProductSet[]>(`/?pageId=${encodeURIComponent(pageId)}`, "GET"),
  create: (input: ProductSetInput) => request<ProductSet>("/", "POST", input),
  update: (id: string, input: ProductSetInput) =>
    request<ProductSet>(`/${encodeURIComponent(id)}`, "PATCH", input),
  remove: (id: string) =>
    request<{ ok: true }>(`/${encodeURIComponent(id)}`, "DELETE"),
  catalog: (
    params: {
      pageId: string;
      q?: string;
      limit?: number;
      offset?: number;
      groupByProduct?: boolean;
      readiness?: "ready" | "zero" | "preorder";
      variantIds?: string[];
    },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams({ pageId: params.pageId });
    if (params.q) query.set("q", params.q);
    if (params.limit != null) query.set("limit", String(params.limit));
    if (params.offset != null) query.set("offset", String(params.offset));
    if (params.groupByProduct) query.set("groupByProduct", "true");
    if (params.readiness) query.set("readiness", params.readiness);
    if (params.variantIds?.length)
      query.set("variantIds", params.variantIds.join(","));
    return request<CatalogListResponse>(
      `/catalog?${query.toString()}`,
      "GET",
      undefined,
      signal,
    );
  },
};
